import { inject, Service } from '@angular/core';
import type { OnyxCustomToastOptions } from '@onyx/angular';
import { OnyxToastMessage, OnyxToastService } from '@onyx/angular';
import {
  cloneDeepWith,
  isArray,
  isEmpty,
  isObject,
  isPlainObject,
} from 'lodash';
import { DateTime, Duration } from 'luxon';
import type { Observable } from 'rxjs';
import {
  finalize,
  interval,
  isObservable,
  merge,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { AuthStore } from '../../auth/common/stores/auth.store';

interface CacheItem<T> {
  value: T;
  expiry: string;
}

@Service()
export class CacheService {
  private readonly authStore = inject(AuthStore);
  private readonly toastService = inject(OnyxToastService);

  private pendingRequests$ = new Map<string, Observable<any>>();
  private cachedRequests = new Map<string, any>();

  constructor() {
    this.clean();
  }

  public get<T>(
    key: string,
    toastOptions?: OnyxCustomToastOptions | false,
  ): T | null {
    const item: CacheItem<T> | null = JSON.parse(localStorage.getItem(key)!);
    if (item == null) return null;

    const expired = DateTime.fromISO(item.expiry) < DateTime.utc();
    if (expired) {
      this.delete(key);
      return null;
    }

    if (toastOptions !== false) {
      this.toastService.showCustom(
        OnyxToastMessage.FORM_CHANGES_RESTORED,
        toastOptions,
      );
    }
    return item.value;
  }

  public set<T>(
    key: string,
    value: T,
    ttl = Duration.fromObject({ minutes: 60 }),
  ): void {
    const cacheValue = cloneDeepWith(value, (value) => {
      const isEmptyOrFile = (value: unknown): boolean => {
        return (
          value instanceof File || (isPlainObject(value) && isEmpty(value))
        );
      };

      if (isArray(value) && value.every((item) => isEmptyOrFile(item))) {
        return [];
      } else if (isEmptyOrFile(value)) {
        return null;
      }

      return undefined;
    });

    localStorage.setItem(
      key,
      JSON.stringify({
        value: cacheValue,
        expiry: DateTime.utc().plus(ttl).toISO(),
      } satisfies CacheItem<T>),
    );
  }

  public cacheRequest<T>(
    key: string,
    request$: Observable<T>,
    ttl: Observable<unknown> | Duration = Duration.fromObject({ minutes: 5 }),
  ): Observable<T> {
    return timer(1).pipe(
      switchMap(() => {
        const cache = this.cachedRequests.get(key);
        if (cache) return of(cache);

        let pendingRequest$ = this.pendingRequests$.get(key);
        if (pendingRequest$) return pendingRequest$;

        pendingRequest$ = request$.pipe(
          tap((result) => this.cachedRequests.set(key, result)),
          finalize(() => this.pendingRequests$.delete(key)),
          shareReplay(1),
        );
        this.pendingRequests$.set(key, pendingRequest$);

        const clear$ = isObservable(ttl) ? ttl : interval(ttl.toMillis());
        merge(clear$, this.authStore.logout$)
          .pipe(take(1))
          .subscribe(() => {
            this.pendingRequests$.delete(key);
            this.cachedRequests.delete(key);
          });

        return pendingRequest$;
      }),
    );
  }

  public delete(key: string): void {
    localStorage.removeItem(key);
  }

  private clean(): void {
    const isCacheItem = (item: unknown): item is CacheItem<unknown> => {
      if (!isObject(item)) return false;
      return 'value' in item && 'expiry' in item;
    };

    for (const key of Object.keys(localStorage)) {
      let item;
      try {
        const value = localStorage.getItem(key)!;
        item = JSON.parse(value);
      } catch {
        continue;
      }

      if (!isCacheItem(item)) continue;
      if (DateTime.fromISO(item.expiry) < DateTime.now()) this.delete(key);
    }
  }
}
